import axios, { AxiosError } from "axios";
import { coreAuthUrls } from "./url";
import i18n from "../../i18n";
import { toast } from "react-toastify";
import { readEnv } from "../../readEnv";
import { refreshAccessToken } from "./client/jmf/coreAuthClient";
import { resetUserSlice } from "../../redux/userSlice";
import { store } from "../../redux/general/reduxStore";
import update = toast.update;

export const taxGuruAxios = axios.create({
    baseURL: readEnv("TAX_GURU_BACKEND_URL"),
    headers: {
        "Accept-Language": i18n.language,
    },
    withCredentials: true,
});

const taxGuruAxiosNoIntercept = axios.create({
    baseURL: readEnv("TAX_GURU_BACKEND_URL"),
    headers: {
        "Accept-Language": i18n.language,
    },
    withCredentials: true,
});

const redirectToLogin = () => {
    store.dispatch(resetUserSlice());
    throw new axios.Cancel("Token invalid, redirecting to login");
};

const createToast = (error: AxiosError<{ title?: string; messageKey?: string; detail?: string }>) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { toastId, noErrorToast } = error.config;
    if (noErrorToast === true || error.code === "ERR_CANCELED") {
        return;
    }

    const title = error?.response?.data?.title;
    const messageKey = error?.response?.data?.messageKey;
    const detail = error?.response?.data?.detail;
    let reasonTranslation: string;
    if (error.code === "ERR_NETWORK") {
        reasonTranslation = i18n.t(`axios.${error.code}`);
    } else if (messageKey && i18n.exists(messageKey)) {
        reasonTranslation = i18n.t(messageKey);
    } else if (title === "Internal Server Error") {
        reasonTranslation = detail ?? i18n.t("common.results.unknownError");
    } else {
        console.error(error);
        reasonTranslation = title ?? i18n.t("common.results.unknownError");
    }
    // TODO display detail somehow
    if (toastId) {
        update(toastId, {
            type: "error",
            render: reasonTranslation,
            isLoading: false,
            autoClose: 5000,
            data: error,
            closeButton: true,
            style: { background: "#d32f2f" },
            closeOnClick: true,
        });
    } else {
        toast(reasonTranslation, {
            closeButton: true,
            closeOnClick: true,
            style: { background: "#d32f2f" },
            type: "error",
            autoClose: 5000,
            isLoading: false,
            toastId: toastId ?? `${error.code}/${messageKey}`,
            data: error,
        });
    }
};

// Add a response interceptor
taxGuruAxios.interceptors.response.use(
    (response) => {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
    },
    (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        if ((error.request.responseURL?.indexOf(coreAuthUrls.login) ?? 0) >= 0) {
            createToast(error);
            return Promise.reject(error);
        }
        if (error.response?.status !== 401) {
            createToast(error);
            return Promise.reject(error);
        }

        const { config } = error;
        return refreshAccessToken(taxGuruAxiosNoIntercept)
            .then(() => {
                return taxGuruAxiosNoIntercept.request(config);
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    toast.dismiss();
                    redirectToLogin();
                } else {
                    createToast(err);
                    return Promise.reject(err);
                }
            });
    }
);
